/* ==========================================================================
   Tintsy paint studio
   Repaints a real photograph by multiplying it through a wall mask, so the
   room's shadows, light and wall texture survive the color change. The mask
   is derived from a matched before/after render pair, which is why the
   furniture never takes the coat.
   ========================================================================== */
(function () {
  'use strict';

  function init(root) {
    if (root.dataset.tzReady === '1') return;
    root.dataset.tzReady = '1';

    var canvas = root.querySelector('[data-tz-canvas]');
    var chipRow = root.querySelector('[data-tz-chips]');
    if (!canvas || !chipRow) return;

    var ctx = canvas.getContext('2d', { alpha: false });
    var dot = root.querySelector('[data-tz-dot]');
    var spec = root.querySelector('[data-tz-spec]');
    var live = root.querySelector('[data-tz-live]');
    var chips = Array.prototype.slice.call(chipRow.querySelectorAll('[data-tz-chip]'));
    if (!chips.length) return;

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var W = canvas.width, H = canvas.height;

    function make() { var c = document.createElement('canvas'); c.width = W; c.height = H; return c; }
    var baseCv = make(), maskCv = make(), curCv = make(), nextCv = make(), tmpCv = make();

    var baseImg = new Image(), maskImg = new Image();
    baseImg.crossOrigin = maskImg.crossOrigin = 'anonymous';
    var loaded = 0, ready = false;
    var start = 0;
    for (var s = 0; s < chips.length; s++) {
      if (chips[s].getAttribute('aria-checked') === 'true') { start = s; break; }
    }
    var current = start, animId = null;

    function onload() {
      if (++loaded < 2) return;
      ready = true;
      baseCv.getContext('2d').drawImage(baseImg, 0, 0, W, H);
      maskCv.getContext('2d').drawImage(maskImg, 0, 0, W, H);
      build(curCv, chips[start].dataset.hex || '');
      ctx.drawImage(curCv, 0, 0);
      label(chips[start]);
      nudge();
    }
    baseImg.onload = maskImg.onload = onload;
    baseImg.src = root.dataset.base;
    maskImg.src = root.dataset.mask;

    /* Canvas 'multiply' honours the mask's alpha, so
       co = a*(Cb*Cs) + (1-a)*Cb — a coat of paint over a lit wall,
       with zero effect where the mask is transparent. */
    function build(target, hex) {
      var t = target.getContext('2d');
      t.globalCompositeOperation = 'source-over';
      t.clearRect(0, 0, W, H);
      t.drawImage(baseCv, 0, 0);
      if (!hex) return;
      var m = tmpCv.getContext('2d');
      m.globalCompositeOperation = 'source-over';
      m.clearRect(0, 0, W, H);
      m.drawImage(maskCv, 0, 0);
      m.globalCompositeOperation = 'source-in';
      m.fillStyle = hex;
      m.fillRect(0, 0, W, H);
      t.globalCompositeOperation = 'multiply';
      t.drawImage(tmpCv, 0, 0);
      t.globalCompositeOperation = 'source-over';
    }

    function label(chip) {
      var hex = chip.dataset.hex || '', name = chip.dataset.name || '', code = chip.dataset.code || '';
      if (dot) { dot.style.background = hex || 'transparent'; dot.style.borderStyle = hex ? 'solid' : 'dashed'; }
      if (spec) spec.textContent = hex ? name + ' · ' + code : name;
      if (live) live.textContent = hex ? 'Wall painted in ' + name + ', ' + code : 'Wall shown unpainted, as photographed';
    }

    function swap() { var t = curCv; curCv = nextCv; nextCv = t; }

    function select(i) {
      if (!ready || (i === current && !animId)) return;
      if (animId) { cancelAnimationFrame(animId); animId = null; ctx.drawImage(nextCv, 0, 0); swap(); }
      current = i;
      chips.forEach(function (el, j) {
        el.setAttribute('aria-checked', j === i ? 'true' : 'false');
        el.tabIndex = j === i ? 0 : -1;
      });
      label(chips[i]);
      build(nextCv, chips[i].dataset.hex || '');

      if (reduced) { ctx.drawImage(nextCv, 0, 0); swap(); return; }

      /* three roller passes, staggered, top to bottom */
      var t0 = performance.now(), D = 760, stag = [0, 150, 300], bw = Math.ceil(W / 3);
      function ease(t) { return t < 0 ? 0 : t > 1 ? 1 : 1 - Math.pow(1 - t, 3); }
      function frame(now) {
        ctx.drawImage(curCv, 0, 0);
        var done = true;
        for (var k = 0; k < 3; k++) {
          var p = ease((now - t0 - stag[k]) / D);
          if (p < 1) done = false;
          var h = Math.round(p * H);
          if (h < 1) continue;
          var x = k * bw, w = Math.min(bw, W - x);
          ctx.drawImage(nextCv, x, 0, w, h, x, 0, w, h);
        }
        if (done) { animId = null; ctx.drawImage(nextCv, 0, 0); swap(); }
        else animId = requestAnimationFrame(frame);
      }
      animId = requestAnimationFrame(frame);
    }

    chips.forEach(function (chip, i) {
      chip.tabIndex = i === start ? 0 : -1;
      chip.addEventListener('click', function () { select(i); });
    });

    chipRow.addEventListener('keydown', function (e) {
      var d = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1
        : (e.key === 'ArrowLeft' || e.key === 'ArrowUp') ? -1 : 0;
      if (!d) return;
      e.preventDefault();
      var n = (current + d + chips.length) % chips.length;
      select(n);
      chips[n].focus();
    });

    /* one unprompted coat on first view, so the gesture is discoverable */
    function nudge() {
      if (reduced || !('IntersectionObserver' in window) || chips.length < 2) return;
      var done = false;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting && !done) {
            done = true; io.disconnect();
            var next = (current + 1) % chips.length;
            setTimeout(function () { select(next); }, 520);
          }
        });
      }, { threshold: .45 });
      io.observe(canvas);
    }
  }

  function boot() { document.querySelectorAll('[data-tz-paint]').forEach(init); }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-tz-paint]').forEach(init);
  });

  /* scroll reveal, shared by every Tintsy section */
  function reveals() {
    var els = document.querySelectorAll('.tz-reveal:not(.tz-in)');
    if (!els.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('tz-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { en.target.classList.add('tz-in'); io.unobserve(en.target); }
      });
    }, { threshold: .12, rootMargin: '0px 0px -40px 0px' });
    els.forEach(function (el) { io.observe(el); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', reveals);
  else reveals();
  document.addEventListener('shopify:section:load', reveals);
})();
